import {
  computed,
  DestroyRef,
  effect,
  Inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  StateKey,
  TransferState,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';

import { BaseViewModel } from '../base-view-model';
import { Constants } from '@helper/constants';
import { SearchProductsRequest, SearchProductsResponse } from '@proto/store/api/store/search';
import { GridItem } from '@common/product-grid/model';
import { SearchRepositoryService } from '@service/search-repository.service';
import { WindowDetectService } from '@service/window-detect.service';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { ModeSelectorModel } from '@common/mode-selector/mode-selector.model';
import { EmptySearchModel } from '@common/empty-search/empty-search.model';
import { SafeStorageService } from '@service/safe-storage.service';
import { MetaData } from '@helper/meta-data';
import { EventTracker } from '../../analytics/event-tracker.service';

import { map, of, switchMap, tap } from 'rxjs';
import { isEqual, findLastIndex } from 'lodash-es';

@Injectable()
export class SearchModel extends BaseViewModel<SearchProductsResponse | undefined> {
  readonly all = 'ALL';
  readonly searchHistoryLimit = 10;
  readonly itemGap = computed(() => (this.modeSelectorModel.mode() ? 39 : 43));
  readonly colorSizeBlockMargin = computed(() => (this.modeSelectorModel.mode() ? 20 : 15));
  readonly nameMap = computed(
    () => {
      const map = new Map<string, string>();
      map.set(this.all, $localize`全部`);
      this.apiResult()
        ?.results?.filter(item => item.big)
        ?.forEach(item => map.set(item.big?.nameEn ?? '', item.big?.name ?? ''));
      return map;
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly categories = computed(() => {
    const list = this.apiResult()?.results.map(res => res.big?.nameEn ?? '') ?? [];
    if (list.length && !this.windowDetectService.isMobileDevice()) {
      list.unshift(this.all);
    }
    return list;
  });
  readonly displayCategories = computed(() => this.apiResult()?.results.map(res => res.big?.name ?? '') ?? []);
  readonly itemGridMap = computed(
    () => {
      const map = this.apiResult()?.results.reduce((map: Map<string, GridItem[]>, item) => {
        const gridItems = GridItem.toGridItems(item.content, this.modeSelectorModel.mode());
        map.set(item.big?.nameEn ?? '', gridItems);
        if (!this.windowDetectService.isMobileDevice()) {
          map.set(this.all, (map.get(this.all) ?? []).concat(gridItems));
        }
        return map;
      }, new Map<string, GridItem[]>());
      if (!this.windowDetectService.isMobileDevice()) {
        map?.set(
          this.all,
          map
            ?.get(this.all)
            ?.filter(
              (i, index, array) =>
                index ===
                findLastIndex(array, s => `${s.meta?.productId}${s.colorId}` === `${i.meta?.productId}${i.colorId}`)
            )
            ?.sort((a, b) => (a.meta?.sequence ?? 0) - (b.meta?.sequence ?? 0)) ?? []
        );
      }
      return map;
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly currGrid = computed(() => this.itemGridMap()?.get(this.currCategory())?.slice(0, this.itemCount()) ?? [], {
    equal: (a, b) => isEqual(a, b),
  });
  readonly currCategoryCount = computed(() => this.itemGridMap()?.get(this.currCategory())?.length ?? 0);
  readonly aspectRatio = computed(() => {
    const category = this.apiResult()?.results?.find(i => i.big?.nameEn === this.currCategory());
    return (
      (this.modeSelectorModel.mode() ? category?.content?.listAspectRatio : category?.content?.clothAspectRatio) ??
      Math.max(
        ...(this.apiResult()?.results?.map(
          r => (this.modeSelectorModel.mode() ? r?.content?.listAspectRatio : r?.content?.clothAspectRatio) ?? 0
        ) ?? [])
      ) ??
      1
    );
  });
  readonly historyKeywords: WritableSignal<string[]>;
  readonly searchKeywords = computed(
    () => {
      const keyword = this.keyword().toLowerCase();
      return Array.from(
        new Set(
          this.emptySearchModel.apiResult().keywords.filter(k => this.keyword() && k.toLowerCase().includes(keyword))
        )
      );
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly currCategory = signal('');
  readonly keyword = signal('');
  readonly shopWindowId = signal('');
  readonly searchNotFound = signal(false);
  readonly showSearchResult = signal(false);
  readonly hotId = signal('');

  private readonly apiResultKey: StateKey<SearchProductsResponse> = makeStateKey('apiResult');

  constructor(
    private modeSelectorModel: ModeSelectorModel,
    private lativRepository: SearchRepositoryService,
    private windowDetectService: WindowDetectService,
    private emptySearchModel: EmptySearchModel,
    private location: Location,
    private eventTracker: EventTracker,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    private localService: SafeStorageService
  ) {
    super(undefined);
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(this.apiResultKey)) {
      // 如果目前搜尋結果頁是SSR, 就從 transferState 拿資料.
      this.setApiResult(this.transferState.get(this.apiResultKey, undefined));
      this.transferState.remove(this.apiResultKey);
    }
    effect(() => {
      if (isPlatformBrowser(this.platformId) && this.currCategory()) {
        window.scrollTo({ top: 0 });
      }
    });
    effect(() => this.showSearchResult.set(!!this.apiResult()?.results.length));
    effect(() => {
      if (!this.showSearchResult()) {
        this.hotId.set('');
      }
    });
    this.historyKeywords = signal(localService.getKeywords() ?? [], {
      equal: (a: string[], b: string[]) => isEqual(a, b),
    });
  }

  changeCategory(index?: number, category = ''): void {
    // 搜尋結果頁, shopWindow頁, 切換上方分類時, 需 replaceState.
    category = index !== undefined && index >= 0 ? this.categories()[index] : category;
    this.currCategory.set(category);
    this.location.replaceState(
      `${this.getRoute(this.keyword(), this.currCategory(), this.shopWindowId())}${window.location.search}`
    );
  }

  removeKeywords(): void {
    this.historyKeywords.set([]);
    this.localService.setKeywords([]);
  }

  searchProducts(
    ref: DestroyRef,
    navigate: boolean,
    keyword?: string,
    hotId?: string,
    bigNameEn?: string,
    setHistory?: boolean,
    clearKeyword?: boolean,
    adId?: string,
    adSeq?: number
  ): void {
    // 空白關鍵字, 不搜尋.
    keyword = decodeURIComponent(keyword?.trim() || '');
    if (!keyword && !hotId) {
      return;
    }
    this.currCategory.set(bigNameEn ?? '');
    (hotId
      ? of(hotId)
      : this.lativRepository.listSearchKeywords().pipe(map(res => res.hotKeywords.find(k => k.keyword === keyword)?.id))
    )
      .pipe(
        tap(hot => (hotId = hot)),
        switchMap(hot => {
          // 如果搜尋的關鍵字, 匹配熱門關鍵字, 會以熱門關鍵字去搜尋資料.
          this.hotId.set(hot || '');
          const key = this.getStateKey(keyword, hotId || hot, adId, adSeq);
          if (!navigate && this.transferState.hasKey(key)) {
            // 已經有之前搜尋結果，直接使用
            const result = this.transferState.get(key, undefined);
            if (result) {
              return of(result);
            }
          }
          return this.lativRepository.searchProducts(
            SearchProductsRequest.fromPartial({
              keyword: hot ? undefined : keyword,
              hotId: hot,
              recordHit: !navigate,
              adId,
              adSeq,
            })
          );
        }),
        takeUntilDestroyed(ref)
      )
      .subscribe({
        next: data =>
          this.setSearchResult(data, navigate, data.title, bigNameEn, setHistory, clearKeyword, hotId, adId, adSeq),
        error: err => this.setApiError(err),
      });
  }

  searchShopWindow(ref: DestroyRef, shopWindowId: string, bigNameEn?: string, adId?: string, adSeq?: number): void {
    this.shopWindowId.set(shopWindowId);
    this.currCategory.set(bigNameEn ?? '');
    this.lativRepository
      .searchProducts(
        SearchProductsRequest.fromPartial({
          shopWindowId,
          recordHit: true,
          adId,
          adSeq,
        })
      )
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: data => this.setSearchResult(data, false, undefined, bigNameEn),
        error: err => this.setApiError(err),
      });
  }

  getRoute(keyword: string, bigNameEn: string, shopWindowId: string): string {
    // 搜尋結果, 有 showWindow, 跟關鍵字搜尋. 兩種路由.
    // 依照不同參數, 回傳對應路徑.
    if (shopWindowId) {
      return RouteHelper.shopWindow(shopWindowId, bigNameEn);
    }
    return RouteHelper.search(keyword, bigNameEn);
  }

  private setSearchResult(
    data: SearchProductsResponse,
    navigate: boolean,
    keyword?: string,
    bigNameEn?: string,
    setHistory?: boolean,
    clearKeyword?: boolean,
    hotId?: string,
    adId?: string,
    adSeq?: number
  ): void {
    this.keyword.set(keyword ?? '');
    if (data.results.length) {
      this.eventTracker.onSearch(this.keyword(), true);
      const bigName = data.results.find(r => r.big?.nameEn === bigNameEn)?.big?.nameEn;
      if (this.windowDetectService.isMobileDevice()) {
        // 手機版, 預設大分類是第一筆資料.
        this.currCategory.set(bigName || data.results[0].big?.nameEn || '');
      } else {
        // 電腦版, 預設大分類是 ALL.
        this.currCategory.set(bigName || this.all);
      }
      // 搜尋的關鍵字有資料才需要出現在歷史紀錄.
      if (setHistory) {
        this.historyKeywords.update(h => {
          if (!h.includes(data.title)) {
            h.unshift(data.title);
          }
          return [...h].slice(0, this.searchHistoryLimit);
        });
        this.localService.setKeywords(this.historyKeywords());
      }
      if (isPlatformServer(this.platformId)) {
        // 如果目前搜尋結果頁是SSR, 就把 API 結果塞到 transferState.
        this.transferState.set<SearchProductsResponse>(this.apiResultKey, data);
      }
      if (navigate) {
        // 儲存navigate之前的state，避免轉頁以後又打一次
        this.transferState.set<SearchProductsResponse>(this.getStateKey(keyword, hotId, adId, adSeq), data);
        this.router.navigate([this.getRoute(this.keyword(), this.currCategory(), this.shopWindowId())]);
      }
    } else {
      this.eventTracker.onSearch(this.keyword(), false);
      this.searchNotFound.set(true);
      if (!this.windowDetectService.isMobileDevice()) {
        // 電腦版，進入搜尋失敗頁面
        this.router.navigate([this.getRoute(this.keyword(), this.currCategory(), '')]);
      }
    }
    if (clearKeyword) {
      this.keyword.set('');
    }
    this.setApiResult(data);
  }

  private getStateKey(
    keyword?: string,
    hotId?: string,
    adId?: string,
    adSeq?: number
  ): StateKey<SearchProductsResponse> {
    return makeStateKey(`${keyword}_${hotId}_${adId}_${adSeq}`);
  }

  override getMetaByUrl(segments: string[], model: SearchProductsResponse | undefined): MetaData | undefined {
    if (segments[0] !== RoutePaths.SEARCH && segments[0] !== RoutePaths.SHOP_WINDOW) {
      return undefined;
    }
    if (segments[0] === RoutePaths.SHOP_WINDOW) {
      return new MetaData(model?.title ?? '', '', model?.results?.[0]?.content?.items[0]?.listHash || '');
    }
    return new MetaData(
      !segments[1] ? $localize`搜尋商品` : $localize`搜尋：${model?.title || decodeURIComponent(segments[1])}`,
      '',
      model?.results?.[0]?.content?.items[0]?.listHash || ''
    );
  }
}
